import React from 'react';
import { Pagination, Empty, Tooltip, Spin } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { ActivityLog } from './ActivityLog.types';
import {
  DEPARTMENT_CONFIG,
  USER_TYPE_CONFIG,
  STATUS_CONFIG,
  formatDateTime,
  getUserInitials,
  getIcon,
} from './ActivityLogSampleData';

interface Props {
  logs: ActivityLog[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onSelect: (log: ActivityLog) => void;
}

const LogRow: React.FC<{ log: ActivityLog; onView: () => void }> = ({ log, onView }) => {
  const deptCfg = DEPARTMENT_CONFIG[log.department] ?? {
    iconKey: 'export',
    color: '#533AB7',
    bg: '#EEEDFE',
    label: log.department,
  };
  const userCfg = USER_TYPE_CONFIG[log.userType] ?? {
    iconKey: 'user',
    color: '#185FA5',
    bg: '#E6F1FB',
    label: log.userType,
  };
  const statusCfg = STATUS_CONFIG[log.status];
  const { date, time } = formatDateTime(log.timestamp);
  const initials = getUserInitials(log.userName);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #f5f5f5',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '#f7f8ff';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
    >
      {/* ── Avatar ── */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: userCfg.bg,
          border: `2px solid ${userCfg.color}33`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 12,
          color: userCfg.color,
          flexShrink: 0,
          letterSpacing: '0.03em',
          marginRight: 14,
        }}
        title={log.userName}
      >
        {initials}
      </div>

      {/* ── Identity: name + user-type chip + dept chip ── */}
      <div style={{ width: 200, flexShrink: 0, marginRight: 20 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#1a1a2e',
            marginBottom: 4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {log.userName}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 10,
              fontWeight: 500,
              color: userCfg.color,
              background: userCfg.bg,
              borderRadius: 5,
              padding: '1px 6px',
              whiteSpace: 'nowrap',
            }}
          >
            {getIcon(userCfg.iconKey, { fontSize: 10 })}
            {userCfg.label}
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 10,
              fontWeight: 500,
              color: deptCfg.color,
              background: deptCfg.bg,
              borderRadius: 5,
              padding: '1px 6px',
              whiteSpace: 'nowrap',
            }}
          >
            {getIcon(deptCfg.iconKey, { fontSize: 10 })}
            {deptCfg.label}
          </span>
        </div>
      </div>

      {/* ── Event: action title + status badge + date/entity ── */}
      <div style={{ width: 240, flexShrink: 0, marginRight: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#1a1a2e',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {log.action}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              background: statusCfg.bg,
              color: statusCfg.color,
              borderRadius: 5,
              padding: '1px 6px',
              flexShrink: 0,
            }}
          >
            {statusCfg.label}
          </span>
        </div>
        <div style={{ fontSize: 11, color: '#aaa' }}>
          {date} · {time}
          {log.affectedEntity && (
            <>
              {' · '}
              <code
                style={{
                  fontSize: 10,
                  background: '#f5f5f5',
                  padding: '1px 4px',
                  borderRadius: 3,
                  color: '#888',
                }}
              >
                {log.affectedEntity}
              </code>
            </>
          )}
        </div>
      </div>

      {/* ── Description ── */}
      <div
        style={{
          flex: 1,
          fontSize: 12,
          color: '#777',
          lineHeight: 1.5,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical' as const,
          marginRight: 16,
        }}
      >
        {log.description}
      </div>

      {/* ── Eye button ── */}
      <Tooltip title="View details" placement="left">
        <button
          onClick={onView}
          aria-label="View log details"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 8,
            border: '1px solid #eee',
            background: '#fafafa',
            cursor: 'pointer',
            color: '#bbb',
            transition: 'all 0.15s',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            const b = e.currentTarget;
            b.style.background = '#eef0ff';
            b.style.borderColor = '#c5caff';
            b.style.color = '#534AB7';
          }}
          onMouseLeave={(e) => {
            const b = e.currentTarget;
            b.style.background = '#fafafa';
            b.style.borderColor = '#eee';
            b.style.color = '#bbb';
          }}
        >
          <EyeOutlined style={{ fontSize: 14 }} />
        </button>
      </Tooltip>
    </div>
  );
};

const ActivityLogTimeline: React.FC<Props> = ({ logs, total, loading, page, pageSize, onPageChange, onSelect }) => {
  return (
    <>
      {/* Column header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 16px 8px',
          fontSize: 10,
          fontWeight: 700,
          color: '#c0c0c0',
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          borderBottom: '1px solid #ebebeb',
          marginBottom: 2,
        }}
      >
        <div style={{ width: 54, flexShrink: 0 }} />
        <div style={{ width: 200, flexShrink: 0, marginRight: 20 }}>User</div>
        <div style={{ width: 240, flexShrink: 0, marginRight: 20 }}>Event</div>
        <div style={{ flex: 1 }}>Description</div>
        <div style={{ width: 32, flexShrink: 0 }} />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Spin />
        </div>
      ) : logs.length === 0 ? (
        <Empty description="No activity logs match your filters" style={{ margin: '48px 0' }} />
      ) : (
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #f0f0f0',
            overflow: 'hidden',
          }}
        >
          {logs.map((log) => (
            <LogRow key={log.id} log={log} onView={() => onSelect(log)} />
          ))}
        </div>
      )}

      {total > pageSize && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <Pagination
            current={page}
            total={total}
            pageSize={pageSize}
            onChange={onPageChange}
            showSizeChanger={false}
            showTotal={(t, range) => `${range[0]}–${range[1]} of ${t}`}
          />
        </div>
      )}
    </>
  );
};

export default ActivityLogTimeline;
